import { Tab, Transition } from "@headlessui/react";
import classNames from "classnames";
import AllInvoices from "./invoice-all.component";
import { Fragment, useEffect, useState, useCallback } from "react";
import {
  fetchInvoiceStatus,
  getAnalyticsData,
  getAssigneeList,
  getRejectionCategory,
} from "src/services/invoice-api";
import { invoiceActions } from "src/context/invoice-slice";
import { useDispatch, useSelector } from "react-redux";
import filterIcon from "src/assets/filterIcon.svg";
import { Button, Input, Modal } from "src/components";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "src/components/ui/select.component";
import { useOutsideClick } from "src/hooks/useOutsideClick";
import { getAllCompanyDetails } from "src/services/company-api";
import { RootState } from "src/context/store";
import InvoiceFiltersComponent from "../../features/fields/container/InvoiceFilters";
import { queryDivisionDetails } from "../../services/division-api";
import { getCityDetails, getDistrictDetails, getStateDetails } from "src/services/region-api";
import InvoicePriorityTab from "./InvoicePriorityTab";
import InvoiceEscalatedTab from "./InvoiceEscalatedTab";
import InvoiceFuidRejectionTab from "./InvoiceFuidRejectionTab";
import InvoiceFeedbackFromExtTab from "./InvoiceFeedbackFromExtTab";
import InvoiceMultiReAssign from "../invoice-master/InvoiceMultiReassign";
import { searchOptions } from "../invoice-master/invoice-helper";
import { useToast } from "src/hooks/useToast";
import useGranularPermission from "src/hooks/useGranularPermission";
import { fineGrainedPermissionsKeys } from "src/constants/permission";

// KAM CN-ADVICE changes: the sub-tab labels are the KEYS of invoiceAnalysisData.entry and the
// panels are positional, so "Feedback from Ext" is appended LAST — hiding it when the role lacks
// the right can then never shift the indices of the four existing tabs.
const FEEDBACK_FROM_EXT_TAB = "Feedback from Ext";

const InvoiceEntry = () => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const dispatch = useDispatch();
  const { InvoiceFiltersData,
    InvoiceFilters,
    selectedInvoices,
    invoiceData,
    invoiceAnalysisData, invoiceEntryTab } = useSelector((state: RootState) => state.invoice);
  const [selectedTab, setSelectedTab] = useState(invoiceEntryTab);
  const [searchCategory, setSearchCategory] = useState("fuid_no");
  const [searchValue, setSearchValue] = useState("");
  const [selectCount, setSelectCount] = useState("");
  const [inputValue, setInputValue] = useState("");    // typing value
  const [isSelected, setIsSelected] = useState(false);
  const [openMultiReassign, setOpenMultiReassign] = useState<boolean>(false);
  const { toast } = useToast();
  // KAM CN-ADVICE changes: View on this right reveals the "Feedback from Ext" sub-tab.
  const { canView: canViewFeedbackFromExt } = useGranularPermission(
    fineGrainedPermissionsKeys.INVOICE["FEEDBACK FROM EXT"],
  );

  const ref = useOutsideClick(() => setIsFilterOpen(false));

  const company_code = InvoiceFilters?.company?.[0]?.code;

  // Fetch initial filter data only once on mount
  useEffect(() => {
    const fetchInitialFilterData = async () => {
      try {
        if (!InvoiceFiltersData?.status?.length) {
          const res = await fetchInvoiceStatus(company_code);  //textract filter
          dispatch(invoiceActions.setInvoiceFiltersData({ key: "status", value: res?.data }));
        }
        if (!InvoiceFiltersData?.assignee?.length) {
          const res = await getAssigneeList(company_code);
          dispatch(invoiceActions.setInvoiceFiltersData({ key: "assignee", value: res?.data?.data }));
        }
        if (!InvoiceFiltersData?.company?.length) {
          const res = await getAllCompanyDetails();
          dispatch(invoiceActions.setInvoiceFiltersData({ key: "company", value: res?.data }));
        }
        if (!InvoiceFiltersData?.city?.length) {
          const res = await getCityDetails("");
          dispatch(invoiceActions.setInvoiceFiltersData({ key: "city", value: res?.data?.data }));
        }
        if (!InvoiceFiltersData?.district?.length) {
          const res = await getDistrictDetails("");
          dispatch(invoiceActions.setInvoiceFiltersData({ key: "district", value: res?.data?.data }));
        }
        if (!InvoiceFiltersData?.state?.length) {
          const res = await getStateDetails("");
          dispatch(invoiceActions.setInvoiceFiltersData({ key: "state", value: res?.data?.data }));
        }
        if (!InvoiceFiltersData?.rejection?.length) {
          const res = await getRejectionCategory(company_code);
          dispatch(invoiceActions.setInvoiceFiltersData({ key: "rejection", value: res?.data?.data }));
        }
      } catch (err) {
        console.error("Error fetching filter data:", err);
      }
    };
    fetchInitialFilterData();
  }, [dispatch]);

  // Fetch divisions when company changes
  useEffect(() => {
    const fetchFilters = async () => {
      if (!InvoiceFilters?.company?.length) {
        dispatch(invoiceActions.setInvoiceFiltersData({ key: "division", value: [] }));
        return;
      }

      try {
        const companyCode = InvoiceFilters.company[0]?.code;

        // Fetch division data
        const divisionRes = await queryDivisionDetails(`/details/?fields=division_name&company_code=${companyCode}`);
        dispatch(invoiceActions.setInvoiceFiltersData({ key: "division", value: divisionRes?.data }));

        // Fetch assignee list
        const assigneeRes = await getAssigneeList(company_code);
        const assigneeList = assigneeRes?.data?.data || [];

        // Set full assignee list
        dispatch(invoiceActions.setInvoiceFiltersData({ key: "assignee", value: assigneeList }));

        // Match current assignee by cognito ID
        const targetCognitoId = InvoiceFilters?.assignee?.[0]?.user_cognitoid;
        if (targetCognitoId) {
          const matchedAssignee = assigneeList.find((item: any) => item.user_cognitoid === targetCognitoId);
          if (matchedAssignee) {
            dispatch(invoiceActions.setInvoiceFilters({ key: "assignee", value: [matchedAssignee] }));
          }
        }
      } catch (err) {
        console.error("Error fetching filters:", err);
      }
    };

    fetchFilters();
  }, [dispatch, InvoiceFilters?.company]);

  //Fetch counts of all invoices
  const fetchDataCount = useCallback(async () => {
    const analyticsData = await getAnalyticsData(company_code)
    dispatch(invoiceActions.setInvoiceAnalysisData(
      {
        entry: {
          "All Invoices": analyticsData?.data?.data?.yet_to_processed_count,
          "Priority Tasks": analyticsData?.data?.data?.yet_to_processed_prioritised,
          "Escalated": analyticsData?.data?.data?.yet_to_processed_escalated,
          "Rejection Review": analyticsData?.data?.data?.rejection_pending,
          // KAM CN-ADVICE changes: the real figure arrives with the first list response
          // (count.count_kam_feedback_pending), same as the Rejection Review badge.
          [FEEDBACK_FROM_EXT_TAB]: 0,
        },
        approval: {
          "All Invoices": analyticsData?.data?.data?.waiting_for_approval_count,
          "Priority Tasks": analyticsData?.data?.data?.waiting_for_approval_prioritised,
          "Escalated": analyticsData?.data?.data?.waiting_for_approval_escalated,
        }
      }
    ));
  }, []);

  useEffect(() => {
    if (Object.values(invoiceAnalysisData?.entry).every((value) => value === 0)) {
      fetchDataCount()
    }
  }, []);

  return (
    <div className="w-full">
      <Transition
        as={Fragment}
        show={isFilterOpen}
        enter="transition ease-in-out duration-200 transform"
        enterFrom="translate-x-full"
        enterTo="translate-x-0"
        leave="transition ease-in-out duration-200 transform"
        leaveFrom="translate-x-0"
        leaveTo="translate-x-full"
      >
        <div className="fixed right-0 inset-0 top-0 z-[500]">
          <InvoiceFiltersComponent
            companyDetails={InvoiceFiltersData?.company || []}
            divisionDetails={InvoiceFiltersData?.division || []}
            stockistDetails={InvoiceFiltersData?.stockist || []}
            statusDetails={InvoiceFiltersData?.status || []}
            assigneeDetails={InvoiceFiltersData?.assignee || []}
            cityDetails={InvoiceFiltersData?.city || []}
            districtDetails={InvoiceFiltersData?.district || []}
            stateDetails={InvoiceFiltersData?.state || []}
            rejectionDetails={selectedTab === 0 || selectedTab === 3 ? InvoiceFiltersData?.rejection || [] : []}
            ref={ref}
            onClose={() => setIsFilterOpen(false)}
          />
        </div>
      </Transition>
      <div className="mx-auto">
        {/* <section className="flex flex-wrap justify-between mx-auto mt-0">
          <InvoiceCard title={tabsDataCount[0]?.count.toString()} heading="Total invoices" />
          <InvoiceCard title={tabsDataCount[2]?.count.toString()} heading="Invoices escalated" />
          <InvoiceCard title={tabsDataCount[1]?.count.toString()} heading="MTD" />
          <InvoiceCard title={tabsDataCount[0]?.count.toString()} heading="Total invoices" />
        </section> */}
        <div className="px-2 py-2 w-full sm:px-0">
          <Tab.Group selectedIndex={selectedTab} onChange={setSelectedTab}>
            <div className="flex justify-between">
              <Tab.List className="flex p-1 space-x-1 rounded-lg bg-[#e3e3e3] w-[880px] h-[38px] items-center">
                {Object.entries(invoiceAnalysisData?.entry)
                  .filter(([name]) => name !== FEEDBACK_FROM_EXT_TAB || canViewFeedbackFromExt)
                  .map(([name, count], index) => (
                    <Tab
                      key={index}
                      className={({ selected }) =>
                        classNames(
                          "w-full rounded-md py-1.5 text-sm font-normal leading-5",
                          "focus:outline-none",
                          selected ? "bg-white" : "hover:bg-white/[0.12] hover:text-black"
                        )
                      }
                    >
                      {name}
                      {count !== null && (
                        <span className="ml-4 bg-[#8051FF] text-white text-[12px] px-2 py-1 rounded-lg">{count}</span>
                      )}
                    </Tab>
                  ))}
              </Tab.List>

              <div className="flex items-center gap-2 h-full">
                {/* M10-11655 */}
                {selectedTab === 0 && (
                  <div className="inline-flex items-center gap-2 bg-white border border-gray-300 rounded-md px-2.5 py-1.5">
                    <span className="text-xs font-medium text-gray-600">Bulk Re-Assign:</span>
                    <input
                      type="number"
                      placeholder="0"
                      value={inputValue}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^\d{0,3}$/.test(value)) {
                          setInputValue(value);
                        }
                      }}
                      className="w-14 text-center text-sm border-none focus:ring-0 focus:outline-none"
                      min="0"
                      max="100"
                    />
                    <Button
                      onClick={() => {
                        const numValue = Number(inputValue);
                        if (!isSelected) {
                          // When selecting
                          if (numValue > 100) {
                            toast({
                              description: "Value cannot be greater than 100",
                              variant: "destructive",
                            });
                            return;
                          }
                          setSelectCount(inputValue);
                          dispatch(invoiceActions.clearSelectedInvoices());
                          setIsSelected(true);
                        } else {
                          // When deselecting
                          setSelectCount("0");
                          setInputValue("")
                          dispatch(invoiceActions.clearSelectedInvoices());
                          setIsSelected(false);
                        }
                      }}
                      className="h-7 px-3 text-xs font-medium text-blue-600 hover:bg-blue-50"
                    >
                      {isSelected ? "Deselect" : "Select"}
                    </Button>
                  </div>)}
                <Button
                  disabled={selectedInvoices.length == 0}
                  onClick={() => setOpenMultiReassign(true)}
                  className="text-sm rounded-lg bg-[#8051FF] text-white"
                >
                  Re-Assign: {selectedInvoices.length}
                </Button>
                {/* M10-11655 */}
                <div className="flex items-center w-[400px] rounded-md overflow-hidden border border-gray-300 bg-white">
                  <Select value={searchCategory} onValueChange={setSearchCategory}>
                    <SelectTrigger className="w-[160px] rounded-none border-none bg-white text-gray-700 focus:ring-0 focus:outline-none focus:shadow-none">
                      <SelectValue placeholder="Search By" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {searchOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder="Search"
                    debounceTime={1250} //M10-11601
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value.trim())}
                    containerClasses="w-full px-3 py-1 text-gray-700 border-none bg-white focus:outline-none focus:ring-0 focus:shadow-none"
                  />
                </div>
                <div className="flex items-center gap-2 h-full relative">
                  {Object.values(InvoiceFilters).some((value: any) => value?.length > 0) && (
                    <span className="absolute top-[-10px] right-[-10px] w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white text-xs">
                      {Object.values(InvoiceFilters).filter((value: any) => value?.length > 0).length}
                    </span>
                  )}
                  <Button className="bg-[#F1F1F1]" onClick={() => setIsFilterOpen(true)}>
                    <img src={filterIcon} alt="filter-icon" />
                  </Button>
                </div>
              </div>
            </div>
            <Tab.Panels className="">
              <Tab.Panel>
                <AllInvoices searchValue={searchValue} searchCategory={searchCategory} selectedCount={selectCount} />
              </Tab.Panel>
              <Tab.Panel>
                <InvoicePriorityTab searchValue={searchValue} searchCategory={searchCategory} />
              </Tab.Panel>
              <Tab.Panel>
                <InvoiceEscalatedTab searchValue={searchValue} searchCategory={searchCategory} />
              </Tab.Panel>
              <Tab.Panel>
                <InvoiceFuidRejectionTab searchValue={searchValue} searchCategory={searchCategory} />
              </Tab.Panel>
              {/* KAM CN-ADVICE changes: last panel, so hiding it leaves indices 0-3 untouched */}
              {canViewFeedbackFromExt && (
                <Tab.Panel>
                  <InvoiceFeedbackFromExtTab searchValue={searchValue} searchCategory={searchCategory} />
                </Tab.Panel>
              )}
            </Tab.Panels>
          </Tab.Group>
        </div>
      </div>
      <Modal isOpen={openMultiReassign} isHeader={false} onCloseModal={() => setOpenMultiReassign(false)} title="Reassign Invoice">
        <InvoiceMultiReAssign
          onSuccess={() => {
          }}
          invoiceData={invoiceData}
          onCloseModal={() => setOpenMultiReassign(false)}
        />
      </Modal>
    </div>
  );
};

export default InvoiceEntry;
